import { ShieldBan, Pencil } from "lucide-react";
import type { TournamentMatch } from "api/matches/types";
import "./styles.css";

export interface MatchFixtureCardProps {
  match: TournamentMatch;
  formatMatchDate: (value: string) => string;
  formatMatchTime: (value: string) => string;
  className?: string;
  maxWidth?: number | string;
  id?: string;
  allMatches?: TournamentMatch[];
  isKnockout?: boolean;
  /** When set, show W/D/L badge for this team's result (win=green, draw=grey, loss=red). Only for completed matches. */
  perspectiveTeamId?: string | null;
  onClick?: (match: TournamentMatch) => void;
  hideInfo?: boolean;
  hideRoundBracket?: boolean;
  /** When true, hide the status label (e.g. "Completed", "Scheduled") in the card header */
  hideStatus?: boolean;
  onSetScore?: (match: TournamentMatch) => void;
}

const MatchFixtureCard = ({
  match,
  formatMatchDate,
  formatMatchTime,
  className = "",
  maxWidth,
  id,
  allMatches = [],
  perspectiveTeamId,
  onClick,
  hideInfo = false,
  hideRoundBracket = false,
  hideStatus = false,
  onSetScore,
}: MatchFixtureCardProps) => {
  const isCompleted = match.status === "completed";

  // Result from perspective of perspectiveTeamId: W / D / L (only for completed matches)
  const resultBadge =
    isCompleted && perspectiveTeamId
      ? (() => {
        if (match.isDraw) return { letter: "D", className: "match-fixture-card__result-badge--draw" };
        const winnerId = match.winnerTeamId ?? match.winner?.id ?? null;
        if (winnerId === perspectiveTeamId) return { letter: "W", className: "match-fixture-card__result-badge--win" };
        const teamIds = [match.team1Id ?? match.team1?.id, match.team2Id ?? match.team2?.id].filter(Boolean);
        if (teamIds.includes(perspectiveTeamId)) return { letter: "L", className: "match-fixture-card__result-badge--loss" };
        return null;
      })()
      : null;

  // Find parent matches for TBD teams
  const parentMatch1 = match.parentMatchId1 ? allMatches.find((m) => m.id === match.parentMatchId1) ?? null : null;
  const parentMatch2 = match.parentMatchId2 ? allMatches.find((m) => m.id === match.parentMatchId2) ?? null : null;

  // Calculate match number for a match if not available
  const getMatchNumber = (targetMatch: TournamentMatch): number | null => {
    // Calculate match number by finding position in same bracket/round
    if (targetMatch.bracket) {
      const sameBracketMatches = allMatches
        .filter((m) => {
          if (targetMatch.isThirdPlaceMatch) {
            return m.isThirdPlaceMatch;
          }
          return m.bracket === targetMatch.bracket && !m.isThirdPlaceMatch;
        })
        .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

      const index = sameBracketMatches.findIndex((m) => m.id === targetMatch.id);
      return index >= 0 ? index + 1 : null;
    }

    return null;
  };

  // Get parent match title for display
  const getParentMatchTitle = (parentMatch: TournamentMatch | null, isLoser: boolean = false): string => {
    if (!parentMatch) return "";

    const prefix = isLoser ? "Loser of" : "Winner of";
    let matchTitle = "";
    const matchNum = getMatchNumber(parentMatch);

    if (parentMatch.isThirdPlaceMatch) {
      matchTitle = "Third Place";
    } else if (parentMatch.bracket) {
      // Extract match number from bracket or use matchNumber
      const bracketLower = parentMatch.bracket.toLowerCase().trim();
      let roundName = "";

      // Format round name with abbreviated match number
      if (bracketLower.includes("semifinal") || bracketLower === "sf") {
        roundName = matchNum ? `SF${matchNum}` : "SF";
      } else if (bracketLower.includes("quarterfinal") || bracketLower.includes("quarter") || bracketLower === "qf") {
        roundName = matchNum ? `QF${matchNum}` : "QF";
      } else if (bracketLower === "final") {
        roundName = "Final";
      } else {
        // Handle Round of X (R16, R32, R64, etc.)
        const roundMatch = bracketLower.match(/^r(\d+)$|round\s*of\s*(\d+)/i);
        if (roundMatch) {
          const roundNum = roundMatch[1] || roundMatch[2];
          roundName = matchNum ? `R${roundNum}-${matchNum}` : `R${roundNum}`;
        } else if (bracketLower.startsWith("r") && /^\d+$/.test(bracketLower.slice(1))) {
          // Handle bracket like "r32", "r64", etc.
          roundName = matchNum ? `${bracketLower.toUpperCase()}-${matchNum}` : bracketLower.toUpperCase();
        } else {
          // Default fallback
          roundName = matchNum ? `${parentMatch.bracket} ${matchNum}` : parentMatch.bracket;
        }
      }

      matchTitle = roundName.trim();
    } else {
      matchTitle = matchNum ? `Match ${matchNum}` : "Match";
    }

    return `${prefix} ${matchTitle}`.trim();
  };

  // Determine if team1 should show "Loser of" (only for third place match)
  const team1IsLoser = match.isThirdPlaceMatch && parentMatch1 !== null;
  const team2IsLoser = match.isThirdPlaceMatch && parentMatch2 !== null;

  // Get display name for team (use isByeSlot when this slot is empty and the other has a team/parent → bye match)
  const getTeamDisplayName = (
    team: TournamentMatch["team1"],
    parentMatch: TournamentMatch | null,
    isLoser: boolean = false,
    isByeSlot: boolean = false
  ): string => {
    if (team) {
      return team.name;
    }
    if (parentMatch) {
      return getParentMatchTitle(parentMatch, isLoser);
    }
    return isByeSlot ? "BYE" : "TBD";
  };

  const renderTeamRow = (
    team?: TournamentMatch["team1"],
    parentMatch: TournamentMatch | null = null,
    isLoser: boolean = false,
    scoreText?: string,
    isWinner = false,
    isByeSlot = false,
    isPerspectiveTeam = false
  ) => {
    const displayName = getTeamDisplayName(team, parentMatch, isLoser, isByeSlot);
    const hasLogo = Boolean(team?.logo);

    return (
      <div
        className={`match-fixture-card__team-row ${isPerspectiveTeam ? "match-fixture-card__team-row--perspective" : ""}`.trim()}
      >
        <div className="match-fixture-card__team-content">
          <div className={`match-fixture-card__team-badge ${hasLogo ? "has-logo" : ""}`.trim()}>
            {hasLogo ? (
              <img src={team!.logo!} alt={displayName} className="match-fixture-card__team-logo" loading="lazy" decoding="async" />
            ) : (
              <ShieldBan size={12} color="#121212" />
            )}
          </div>
          <span className={`match-fixture-card__team-name ${isWinner ? "is-winner" : ""}`.trim()}>{displayName}</span>
        </div>
        <div className="match-fixture-card__team-end">
          {typeof scoreText === "string" && (
            <span className={`match-fixture-card__score-main ${isWinner ? "is-winner" : ""}`.trim()}>{scoreText}</span>
          )}
          <span
            className={`match-fixture-card__winner-arrow ${isWinner ? "is-visible" : ""}`.trim()}
            aria-hidden="true"
          />
        </div>
      </div>
    );
  };

  const winnerTeamId = match.winner?.id ?? match.winnerTeamId ?? null;
  const shouldShowWinnerIndicator = isCompleted && Boolean(winnerTeamId);
  const team1IsWinner = shouldShowWinnerIndicator && match.team1?.id && winnerTeamId === match.team1.id;
  const team2IsWinner = shouldShowWinnerIndicator && match.team2?.id && winnerTeamId === match.team2.id;

  // Score: when penalties exist, show as "score (penalty)" e.g. "0 (2)"
  const penaltyUnit = match.penalties;
  const showScore = isCompleted;
  const team1ScoreText = showScore
    ? penaltyUnit
      ? `${match.score?.team1 ?? "—"} (${penaltyUnit.team1})`
      : String(match.score?.team1 ?? "—")
    : undefined;
  const team2ScoreText = showScore
    ? penaltyUnit
      ? `${match.score?.team2 ?? "—"} (${penaltyUnit.team2})`
      : String(match.score?.team2 ?? "—")
    : undefined;

  // Get match number for current match
  const currentMatchNumber = getMatchNumber(match);

  // Get status display text and class
  const getStatusDisplay = () => {
    switch (match.status) {
      case "completed":
        return { text: "Completed", className: "match-fixture-card__status--completed" };
      case "ongoing":
        return { text: "Live", className: "match-fixture-card__status--live" };
      case "scheduled":
        return { text: "Scheduled", className: "match-fixture-card__status--scheduled" };
      case "draft":
        return { text: "Draft", className: "match-fixture-card__status--draft" };
      case "cancelled":
        return { text: "Cancelled", className: "match-fixture-card__status--cancelled" };
      default:
        return { text: match.status, className: "" };
    }
  };

  const statusDisplay = getStatusDisplay();
  const isScheduled = match.status === "scheduled";
  const hasTeams = !!(match.team1 && match.team2);
  const isClickable = !!onClick && hasTeams;

  const hasRoundOrGroupOrBadge = !hideRoundBracket || !!match.group || !!resultBadge;
  const showStatusOnLeft = !hideStatus && !hasRoundOrGroupOrBadge;
  const showStatusOnRight = !hideStatus && hasRoundOrGroupOrBadge;
  const hasHeaderLeftContent = hasRoundOrGroupOrBadge || showStatusOnLeft;
  const hasHeaderRightContent = showStatusOnRight || (!hideInfo && !!(isScheduled && onSetScore && hasTeams));
  const hasHeaderContent = hasHeaderLeftContent || hasHeaderRightContent;

  // Get round/bracket display text
  const getRoundBracketText = () => {
    if (match.bracket) {
      const bracketLower = match.bracket.toLowerCase().trim();

      // Handle Semifinal
      if (bracketLower.includes("semifinal") || bracketLower === "sf") {
        return allMatches.length > 0 ? `SF${currentMatchNumber}` : `Semi-Final`;
      }

      // Handle Quarterfinal
      if (
        bracketLower.includes("quarterfinal") ||
        bracketLower.includes("quarter") ||
        bracketLower === "qf"
      ) {
        return allMatches.length > 0 ? `QF${currentMatchNumber}` : `Quarter-Final`;
      }

      if (bracketLower.includes("final")) {
        if (match.isThirdPlaceMatch) {
          return `3rd Place`;
        }
        return `Final`;
      }

      // Handle Round of X (R16, R32, R64, etc.)
      const roundMatch = bracketLower.match(/^r(\d+)$|round\s*of\s*(\d+)/i);
      if (roundMatch) {
        const roundNum = roundMatch[1] || roundMatch[2];
        return allMatches.length > 0 ? `R${roundNum}-${currentMatchNumber}` : `R${roundNum}`;
      }

      // Handle other bracket names - try to extract number if it's a round format
      if (bracketLower.startsWith("r") && /^\d+$/.test(bracketLower.slice(1))) {
        return allMatches.length > 0 ? `${bracketLower.toUpperCase()}-${currentMatchNumber}` : `${bracketLower.toUpperCase()}`;
      }

      // Default fallback
      return `#${currentMatchNumber}`;
    }
    return match.round ? `Round ${match.round}` : `#${currentMatchNumber}`;
  };

  return (
    <div
      id={id}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick!(match); } } : undefined}
      className={`match-fixture-card ${className} ${isClickable ? "match-fixture-card--clickable" : ""} ${!hasHeaderContent ? "match-fixture-card--no-header" : ""}`.trim()}
      style={maxWidth ? { maxWidth } : undefined}
      onClick={isClickable ? () => onClick!(match) : undefined}
    >
      {/* Header: only render when there is something to show (round, group, result badge, status, or set score) */}
      {hasHeaderContent && (
        <div className="match-fixture-card__header">
          <div className="match-fixture-card__header-left">
            {!hideRoundBracket && (
              <span className="match-fixture-card__round-bracket">{getRoundBracketText()}</span>
            )}
            {match.group && (
              <span className="match-fixture-card__group-badge">{match.group.startsWith("Group") ? match.group : `Group ${match.group}`}</span>
            )}
            {resultBadge && (
              <span className={`match-fixture-card__result-badge ${resultBadge.className}`} aria-label={resultBadge.letter === "W" ? "Win" : resultBadge.letter === "D" ? "Draw" : "Loss"}>
                {resultBadge.letter}
              </span>
            )}
            {showStatusOnLeft && (
              <span className={`match-fixture-card__status ${statusDisplay.className}`.trim()}>{statusDisplay.text}</span>
            )}
          </div>
          {(showStatusOnRight || (!hideInfo && !!(isScheduled && onSetScore && hasTeams))) && (
            <div className="match-fixture-card__header-right">
              {!hideInfo && isScheduled && onSetScore && hasTeams && (
                <button
                  className="match-fixture-card__set-score-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetScore(match);
                  }}
                  type="button"
                >
                  <Pencil size={12} />
                  Set Score
                </button>
              )}
              {showStatusOnRight && (
                <span className={`match-fixture-card__status ${statusDisplay.className}`.trim()}>{statusDisplay.text}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Teams and Info with separator */}
      <div className="match-fixture-card__content">
        <div className="match-fixture-card__teams">
          {renderTeamRow(
            match.team1,
            parentMatch1,
            team1IsLoser,
            team1ScoreText,
            Boolean(team1IsWinner),
            !match.team1 && !parentMatch1 && (!!match.team2 || !!parentMatch2),
            perspectiveTeamId != null && (match.team1Id ?? match.team1?.id) === perspectiveTeamId
          )}
          {renderTeamRow(
            match.team2,
            parentMatch2,
            team2IsLoser,
            team2ScoreText,
            Boolean(team2IsWinner),
            !match.team2 && !parentMatch2 && (!!match.team1 || !!parentMatch1),
            perspectiveTeamId != null && (match.team2Id ?? match.team2?.id) === perspectiveTeamId
          )}
        </div>
        {!hideInfo && (
          <>
            <div className="match-fixture-card__divider" />
            <div className="match-fixture-card__info">
              <span className="match-fixture-card__date">{match.startTime ? formatMatchDate(match.startTime) : "TBA"}</span>
              <span className="match-fixture-card__venue">{match.venue?.name ?? "TBA"}</span>
              {!isCompleted && (
                <span className="match-fixture-card__time">{match.startTime ? formatMatchTime(match.startTime) : "TBA"}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MatchFixtureCard;
